import React, { useEffect, useState } from "react";
import { Mail as MailIcon, Hash, LayoutDashboard, FolderClosed, Menu, X, ChevronRight } from "lucide-react";
import { useWM } from "./store";
import { APPS, CATEGORIES } from "./apps";
import { useAuth } from "./AuthContext";
import { mailUnread } from "./mailUnread";

// Apps pinned to the always-visible bottom bar on mobile.
const PRIMARY = [
  { id: "dashboard", label: "Home", Icon: LayoutDashboard },
  { id: "mail",      label: "Mail", Icon: MailIcon },
  { id: "teamchat",  label: "Chat", Icon: Hash },
  { id: "files",     label: "Files", Icon: FolderClosed },
];

/**
 * MobileNav — bottom tab bar + hamburger drawer.
 * Replaces the hover-revealed left Dock on phone-sized screens (<768 px).
 *
 *   ┌──────────────────────────────────────────────┐
 *   │ [Home] [Mail•] [Chat] [Files]      [More ☰]  │  ← fixed bottom
 *   └──────────────────────────────────────────────┘
 */
export default function MobileNav() {
  const { open, windows, focused, focus } = useWM();
  const { user, appAccess } = useAuth();
  const [drawer, setDrawer] = useState(false);
  const [mailBadge, setMailBadge] = useState(0);

  // Live unread for the Mail pill in the bottom bar.
  useEffect(() => {
    const unsub = mailUnread.subscribe((c) => setMailBadge(c.total || 0));
    return () => unsub();
  }, []);

  const launch = (appId) => {
    const app = APPS.find((a) => a.id === appId);
    if (!app) return;
    const existing = windows.find((w) => w.appId === appId);
    if (existing) focus(existing.id);
    else open(app);
    setDrawer(false);
  };

  // The currently-visible app — prefer the actual focused window, otherwise
  // any non-minimized one. Used to highlight the bottom-nav pill.
  const activeAppId = (() => {
    const focusedWin = windows.find((w) => w.id === focused && !w.isMinimized);
    if (focusedWin) return focusedWin.appId;
    const anyVisible = windows.find((w) => !w.isMinimized);
    return anyVisible?.appId;
  })();

  // Drawer apps — every app the user is allowed to launch, grouped by category.
  const isIndividual = user?.role === "individual";
  const visibleApps = APPS.filter((a) => {
    if (a.hideFromDock) return false;
    if (a.requiresSuperAdmin && !user?.is_super_admin) return false;
    if (isIndividual && a.id === "attendance") return false;
    if (appAccess?.hidden?.includes(a.id)) return false;
    return true;
  });

  return (
    <>
      {/* Bottom tab bar */}
      <div
        data-testid="mobile-bottom-nav"
        className="fixed left-0 right-0 bottom-0 z-[200] flex items-stretch"
        style={{
          background: "linear-gradient(180deg, rgba(8,11,22,0.88), rgba(8,11,22,0.96))",
          borderTop: "1px solid rgba(167,139,250,0.25)",
          backdropFilter: "blur(14px)",
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
          height: 58,
        }}
      >
        {PRIMARY.map(({ id, label, Icon }) => {
          const active = activeAppId === id;
          const showBadge = id === "mail" && mailBadge > 0;
          return (
            <button
              key={id}
              data-testid={`mobile-nav-${id}`}
              onClick={() => launch(id)}
              className="flex-1 flex flex-col items-center justify-center gap-0.5 relative"
              style={{
                color: active ? "#a78bfa" : "rgba(199,193,255,0.65)",
                borderTop: active ? "2px solid #a78bfa" : "2px solid transparent",
              }}
            >
              <span className="relative">
                <Icon size={20} strokeWidth={active ? 2.2 : 1.7} />
                {showBadge && (
                  <span
                    data-testid="mobile-mail-badge"
                    className="absolute -top-1 -right-2 min-w-[16px] h-[16px] px-1 rounded-full mono text-[9px] font-bold flex items-center justify-center leading-none"
                    style={{
                      background: "#ff7a2e",
                      color: "#0e1a22",
                      boxShadow: "0 0 8px rgba(255,122,46,0.6)",
                      border: "1.5px solid #0b0f1a",
                    }}
                  >
                    {mailBadge > 99 ? "99+" : mailBadge}
                  </span>
                )}
              </span>
              <span className="mono text-[9px] uppercase tracking-wider">{label}</span>
            </button>
          );
        })}
        <button
          data-testid="mobile-nav-more"
          onClick={() => setDrawer(true)}
          className="flex-1 flex flex-col items-center justify-center gap-0.5"
          style={{ color: drawer ? "#a78bfa" : "rgba(199,193,255,0.65)" }}
        >
          <Menu size={20} strokeWidth={1.7} />
          <span className="mono text-[9px] uppercase tracking-wider">More</span>
        </button>
      </div>

      {/* Hamburger drawer — full-height sheet, slides from left */}
      {drawer && (
        <div
          data-testid="mobile-drawer-scrim"
          className="fixed inset-0 z-[300]"
          style={{ background: "rgba(2,3,8,0.7)", backdropFilter: "blur(4px)" }}
          onClick={() => setDrawer(false)}
        >
          <div
            data-testid="mobile-drawer"
            onClick={(e) => e.stopPropagation()}
            className="absolute left-0 top-0 bottom-0 w-[84%] max-w-[340px] flex flex-col"
            style={{
              background: "linear-gradient(180deg, #0b0f1a 0%, #060810 100%)",
              borderRight: "1px solid rgba(167,139,250,0.25)",
              boxShadow: "8px 0 32px rgba(0,0,0,0.6)",
            }}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/8">
              <div className="flex items-center gap-2">
                <span
                  className="inline-block"
                  style={{
                    width: 14, height: 16,
                    clipPath: "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)",
                    background: "linear-gradient(160deg, #8b5cf6, #0891b2)",
                  }}
                />
                <div className="leading-tight">
                  <div className="mono text-[11px] uppercase tracking-widest text-white">HEXA</div>
                  <div className="mono text-[9px] uppercase tracking-widest text-[#c7c1ff]/55">// all apps</div>
                </div>
              </div>
              <button
                data-testid="mobile-drawer-close"
                onClick={() => setDrawer(false)}
                className="p-1.5 rounded hover:bg-white/8 text-white/70"
              >
                <X size={16} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-2 py-3 space-y-4">
              {/* Pinned (core) apps */}
              <div>
                <div className="mono text-[9px] uppercase tracking-[0.3em] text-[#8b5cf6]/70 px-3 mb-1.5">// core</div>
                {visibleApps.filter((a) => a.category === "core" || a.category === "system").map((a) => (
                  <DrawerTile key={a.id} app={a} onPick={launch} />
                ))}
              </div>

              {CATEGORIES.map((cat) => (
                <div key={cat.id}>
                  <div className="mono text-[9px] uppercase tracking-[0.3em] text-[#8b5cf6]/70 px-3 mb-1.5">// {cat.label}</div>
                  {visibleApps.filter((a) => a.category === cat.id).map((a) => (
                    <DrawerTile key={a.id} app={a} onPick={launch} />
                  ))}
                </div>
              ))}
            </div>

            <div className="px-4 py-3 border-t border-white/8 mono text-[9px] uppercase tracking-widest text-[#c7c1ff]/50">
              {user?.address || ""}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function DrawerTile({ app, onPick }) {
  const Icon = app.icon;
  return (
    <button
      data-testid={`mobile-drawer-app-${app.id}`}
      onClick={() => onPick(app.id)}
      className="w-full flex items-center gap-3 px-3 py-2.5 rounded hover:bg-white/6 text-left"
    >
      <span
        className="inline-flex items-center justify-center flex-shrink-0"
        style={{
          width: 32, height: 36,
          clipPath: "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)",
          background: "linear-gradient(150deg, rgba(139,92,246,0.18), rgba(59,130,246,0.12))",
          border: "1px solid rgba(167,139,250,0.25)",
        }}
      >
        <Icon size={16} color="#c7c1ff" strokeWidth={1.7} />
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-white/90 truncate">{app.title}</div>
        <div className="text-[10px] text-white/45 mono truncate">{app.description}</div>
      </div>
      <ChevronRight size={14} className="text-white/30" />
    </button>
  );
}
